import { useEffect, useState } from "react";
import { debounce } from "../apps/utilities";

const getValidField = (field: any) => {
    if (!field) return null;

    // Case 1: Single element
    if (field.name) return field;

    // Case 2: Collection (RadioNodeList, HTMLCollection, Array)
    if (typeof field.length === "number") {
        const inputs = Array.from(field);

        if (!inputs.length) return null;

        // 1. Prefer checked (radio/checkbox)
        const checked = inputs.find((el: any) => el.checked);
        if (checked) return checked;

        // 2. Prefer visible + enabled (important for Shopify)
        const active = inputs.find((el: any) => !el.disabled && el.offsetParent !== null);
        if (active) return active;

        // 3. Fallback to first
        return inputs[0];
    }

    return null;
};

const detectChange = debounce((...callbacks) => {
    callbacks.forEach((callback) => callback());
}, 150);

export function useShopifyProductState(enable = false) {
    const [variantId, setVariantId] = useState<string | null>(null);
    const [quantity, setQuantity] = useState<number>(1);
    const [sellingPlan, setSellingPlan] = useState<string>("");

    useEffect(() => {
        if (!enable) return;
        const cleanupFns: (() => void)[] = [];

        const productForm = window.kefi_bundles.product.form;

        const getVariantInput = () => getValidField(productForm.id);

        const getQuantityInput = () => getValidField(productForm.quantity);

        const getSellingPlanInput = () =>
            getValidField(productForm.selling_plan || document.querySelector("[name='selling_plan']"));

        const updateVariantFromInput = () => {
            const input = getVariantInput();
            if (!input) return;
            setVariantId(input.value);
        };

        const updateQuantity = () => {
            const input = getQuantityInput();
            if (!input) return;

            const val = parseInt(input.value || "1", 10);
            if (!isNaN(val)) setQuantity(val);
        };

        const interval = setInterval(updateQuantity, 500);

        const updateSellingPlan = () => {
            const input = getSellingPlanInput();
            if (!input) return;
            setSellingPlan(input.value || "");
        };

        detectChange(updateVariantFromInput, updateQuantity, updateSellingPlan);

        // 2️⃣ Listen hidden variant input change
        const variantInput = getVariantInput();
        if (variantInput) {
            const handler = () => updateVariantFromInput();
            variantInput.addEventListener("change", handler);
            cleanupFns.push(() => variantInput.removeEventListener("change", handler));
        }

        // 3️⃣ Listen quantity changes
        const qtyInput = getQuantityInput();
        if (qtyInput) {
            const handler = () => updateQuantity();
            qtyInput.addEventListener("input", handler);
            qtyInput.addEventListener("change", handler);

            cleanupFns.push(() => {
                qtyInput.removeEventListener("input", handler);
                qtyInput.removeEventListener("change", handler);
            });
        }

        const sellingPlanInput = getSellingPlanInput();
        if (sellingPlanInput) {
            const handler = () => updateSellingPlan();
            sellingPlanInput.addEventListener("input", handler);
            sellingPlanInput.addEventListener("change", handler);

            cleanupFns.push(() => {
                sellingPlanInput.removeEventListener("input", handler);
                sellingPlanInput.removeEventListener("change", handler);
            });
        }

        // 5️⃣ MutationObserver (DOM refresh cases)
        const observer = new MutationObserver(() => {
            updateVariantFromInput();
            updateQuantity();
            updateSellingPlan();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
        });

        cleanupFns.push(() => observer.disconnect());

        return () => {
            cleanupFns.forEach((fn) => fn());
            clearInterval(interval);
        };
    }, []);

    return { variantId, quantity, sellingPlan };
}
